import React, { useState, useEffect } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { formatDate } from "../../../Components/Common/FormatDate";
import { Link, useNavigate } from "react-router-dom";
import FranchiseEnquiryService from "../../../services/KangarooServices/FranchiseEnquiryService";
import FranchiseDetail from "../component/FranchiseDetail";
import { Eye } from "lucide-react";
import { Triangle } from "react-loader-spinner";

const FranchiseEnquiry = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [courseData, setCourseData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [filters, setFilters] = useState({ search: "" });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const toggleDetail = (item) => {
    setSelectedItem(item);
    setIsDetailOpen(!isDetailOpen);
  };
  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };
  const fetchData = async (
    page = 1,
    size = pageSize,
    currentFilters = debouncedFilters,
  ) => {
    try {
      const data = await FranchiseEnquiryService.getList({
        ...currentFilters,
        page,
        pageSize: size,
      });

      setCourseData(data?.data);
      setTotalRows(data.totalCount || 0);
    } catch (err) {
      console.error("ERROR:", err);
    }
  };
  // debounce filter
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1); // reset page on new search
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters]);

  // fetch data when page / filters change
  useEffect(() => {
    fetchData(currentPage, pageSize, debouncedFilters);
  }, [currentPage, pageSize, debouncedFilters]);

  // Pagination controls
  const totalPages = Math.ceil(totalRows / pageSize);

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Franchise Enquiry" pageTitle="User Management" />
        <Row>
          <Col>
            <div>
              <button
                className="btn btn-success"
                onClick={() => navigate("/franchise-enquiry/create")}
              >
                + Add
              </button>
            </div>
          </Col>
          <Col>
            <div className="d-md-flex justify-content-sm-end gap-2">
              <div className="search-box ms-md-2 flex-shrink-0 mb-3 mb-md-0">
                <Input
                  placeholder="Search by all"
                  value={filters.search}
                  onChange={handleFilterChange}
                />

                <i className="ri-search-line search-icon"></i>
              </div>
            </div>
          </Col>
        </Row>
        {isLoading ? (
          <div className="my-3 d-flex justify-content-center align-items-center">
            <Triangle height="100" width="100" color="#556ee6" />
          </div>
        ) : (
          <>
            <Row>
              {courseData?.map((item, index) => {
                return (
                  <Col className="mt-4" sm={6} xl={4} key={item.id || index}>
                    <Card className="mb-3 border-0 shadow-sm h-100 d-flex flex-column hover-lift transition">
                      <CardHeader>
                        <Row>
                          <Col md={2}>
                            <div className="avatar-sm">
                              <div className="avatar-title rounded bg-success-subtle text-success fs-14 fw-semibold">
                                {item?.fullName
                                  ?.split(" ")
                                  .slice(0, 2) // take first and last name
                                  .map((name) => name.charAt(0).toUpperCase())
                                  .join("")}
                              </div>
                            </div>
                          </Col>

                          <Col md={8}>
                            <div className="d-flex justify-content-start align-content-center align-items-center ps-2 h-100">
                              <h5 className=" fw-semibold">{item?.fullName}</h5>
                            </div>
                          </Col>
                          <Col md={2}>
                            <div className="d-flex justify-content-end align-content-center align-items-center gap-1">
                              <Button
                                size="sm"
                                color="info"
                                className="btn-soft-info "
                                onClick={() => toggleDetail(item)}
                              >
                                <Eye size={16} />
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </CardHeader>

                      <CardBody className="flex-grow-1 d-flex flex-column">
                        <div className="">
                          <div className="mb-2 d-flex align-items-center">
                            <i className="bx bx-envelope text-primary me-2"></i>
                            <span className="small">{item?.email}</span>
                          </div>

                          <div className="mb-2 d-flex align-items-center">
                            <i className="bx bx-phone text-primary me-2"></i>
                            <span className="small">
                              {item?.phoneCountryCode} {item?.phone}
                            </span>
                          </div>

                          <div className="mb-2 d-flex align-items-center">
                            <i className="bx bx-map-pin text-success me-2"></i>
                            <span className="small">
                              {item?.prefreredLocation}
                            </span>
                          </div>
                          <div className=" d-flex align-items-center">
                            <i className="bx bx-time-five text-success me-2"></i>
                            <span className="small">
                              {formatDate(item?.createdAt)}
                            </span>
                          </div>
                        </div>
                        <hr className="text-muted" />

                        <div className="d-flex justify-content-between align-content-center align-items-center ">
                          <p className="d-flex align-items-center">
                            {item?.message?.length > 100
                              ? item.message.slice(0, 100) + "..."
                              : item?.message}
                          </p>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
            {/* Pagination */}
            {totalPages > 0 && (
              <div className="d-flex justify-content-center my-4 sticky-bottom">
                <Pagination className="mb-0">
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink
                      previous
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                  </PaginationItem>

                  {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                    const page = currentPage <= 4 ? i + 1 : i + currentPage - 4;
                    if (page > totalPages) return null;
                    return (
                      <PaginationItem key={page} active={currentPage === page}>
                        <PaginationLink onClick={() => setCurrentPage(page)}>
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}

                  <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink
                      next
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </PaginationItem>
                </Pagination>
              </div>
            )}
          </>
        )}
        <FranchiseDetail
          isOpen={isDetailOpen}
          toggle={toggleDetail}
          selectedItem={selectedItem}
        />
      </Container>
    </div>
  );
};

export default FranchiseEnquiry;
